import { ApiError, type ApiEnvelope } from './errors'
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from './tokens'

export type { ApiEnvelope } from './errors'

let onUnauthorized: (() => void) | null = null

let refreshPromise: Promise<string> | null = null

export function configureAuth(onUnauth: typeof onUnauthorized) {
  onUnauthorized = onUnauth
}

export async function refreshTokens(): Promise<string> {
  return doRefresh()
}

async function doRefresh(): Promise<string> {
  if (refreshPromise) return refreshPromise

  const rt = getRefreshToken()
  if (!rt) {
    clearTokens()
    onUnauthorized?.()
    throw new Error('no refresh token')
  }

  refreshPromise = (async () => {
    const resp = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    })

    if (!resp.ok) {
      clearTokens()
      onUnauthorized?.()
      throw new Error('refresh failed')
    }

    const body = await resp.json()
    const data = body.data ?? body
    setTokens(data.access_token, data.refresh_token)
    return data.access_token
  })()

  try {
    return await refreshPromise
  } catch {
    clearTokens()
    onUnauthorized?.()
    throw new Error('refresh failed')
  } finally {
    refreshPromise = null
  }
}

async function handleResponse<T>(response: Response, url: string, options: RequestInit): Promise<T> {
  const requestId = response.headers.get('X-Request-Id') ?? undefined

  if (!response.ok) {
    let body: ApiEnvelope | undefined
    try {
      body = await response.json()
    } catch {
      // no body
    }
    const error = ApiError.fromResponse(response.status, body ?? {}, requestId)

    if (error.code === 'UNAUTHORIZED' && !url.includes('/auth/refresh') && !url.includes('/auth/login')) {
      try {
        const newToken = await doRefresh()
        const headers = new Headers(options.headers)
        headers.set('Authorization', `Bearer ${newToken}`)
        const retryResp = await fetch(url, { ...options, headers })
        return handleResponse<T>(retryResp, url, options)
      } catch {
        throw error
      }
    }

    throw error
  }

  if (response.status === 204) {
    return undefined as T
  }

  const body: ApiEnvelope<T> = await response.json()
  return body.data as T
}

async function buildHeaders(options: RequestInit): Promise<Headers> {
  const token = getAccessToken()
  const headers = new Headers(options.headers)

  headers.set('X-Request-Id', crypto.randomUUID())

  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  return headers
}

async function handleUnauthorized<T>(error: ApiError, url: string, options: RequestInit): Promise<T> {
  if (error.code === 'UNAUTHORIZED' && !url.includes('/auth/refresh') && !url.includes('/auth/login')) {
    try {
      const newToken = await doRefresh()
      const headers = await buildHeaders(options)
      headers.set('Authorization', `Bearer ${newToken}`)
      const retryResp = await fetch(url, { ...options, headers })
      return handleResponse<T>(retryResp, url, options)
    } catch {
      throw error
    }
  }
  throw error
}

async function makeRequest(url: string, options: RequestInit = {}): Promise<{ response: Response; options: RequestInit }> {
  const headers = await buildHeaders(options)
  return { response: await fetch(url, { ...options, headers }), options }
}

export async function apiRequest<T>(
  url: string,
  options: RequestInit = {},
): Promise<T> {
  const { response, options: opts } = await makeRequest(url, options)
  return handleResponse<T>(response, url, opts)
}

export async function apiRequestEnvelope<T>(
  url: string,
  options: RequestInit = {},
): Promise<ApiEnvelope<T>> {
  const { response } = await makeRequest(url, options)

  if (!response.ok) {
    const requestId = response.headers.get('X-Request-Id') ?? undefined
    let body: ApiEnvelope | undefined
    try {
      body = await response.json()
    } catch {
      // no body
    }
    const error = ApiError.fromResponse(response.status, body ?? {}, requestId)
    return handleUnauthorized<ApiEnvelope<T>>(error, url, options)
  }

  if (response.status === 204) {
    return {}
  }

  return response.json() as Promise<ApiEnvelope<T>>
}
